import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import type {
  OrigemDoDado,
  Pagina,
  SituacaoDoCredenciado,
} from '@/modulos/operacao/comum/contrato';
import { naoInsistirEmRecusa } from '@/modulos/operacao/comum/dados';
import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';
import { exportarCsv } from '@/nucleo/exportarCsv';
import type { TipoDeCredenciado } from '@/nucleo/pessoaNatureza';

import type {
  Credenciado,
  FiltroDeCredenciados,
  IndicadoresDeCredenciados,
  RegimeTributario,
  VeiculoDoCredenciado,
} from './contrato';

/**
 * A camada de dados dos credenciados — dado real, da API
 * (`bff/v1/operacao/credenciados`, apps/api/src/modulos/credenciados-operacao).
 *
 * Nenhum componente chama `chamarApi` direto, e as chaves de cache moram junto de quem
 * as invalida — o padrao que a tela do conector gov.br estabeleceu
 * (modulos/admin/integracoes/dados.ts).
 *
 * A PAGINACAO, A BUSCA E TODO FILTRO SAO DO SERVIDOR (CLAUDE.md, "Regras de API",
 * item 6), e O RECORTE TAMBEM: quem e de DRE ve so a propria diretoria porque a
 * politica do banco (0012) recorta, nao porque a tela filtra. A tela nao sabe a DRE de
 * ninguem e nao precisa saber.
 *
 * DADO PESSOAL. Na lista e na exportacao, CPF/CNPJ, e-mail e telefone chegam
 * MASCARADOS — o inteiro nao sai do banco. O detalhe traz contatos, endereco e conta
 * por inteiro (a aba Dados Gerais existe para isso), e o banco registra a leitura em
 * `acessos_dado_pessoal`; o documento continua mascarado.
 *
 * So leitura. Nada aqui grava.
 */

export const CAMINHO_DA_API = '/bff/v1/operacao/credenciados';

export const CHAVES_DE_CREDENCIADOS = {
  lista: (filtro: FiltroDeCredenciados) => ['credenciados', 'lista', filtro] as const,
  indicadores: ['credenciados', 'indicadores'] as const,
  detalhe: (slug: string | undefined) => ['credenciados', 'detalhe', slug] as const,
  representantes: (credenciadoId: string | undefined) =>
    ['credenciados', 'detalhe', credenciadoId, 'representantes'] as const,
};

/** Uma linha como a API devolve (`credenciados_operacao_item`, migracao 0360). */
export interface LinhaDaApi {
  id: string;
  slug: string;
  tipo: TipoDeCredenciado;
  status: SituacaoDoCredenciado;
  cpf_cnpj_mascarado: string | null;
  nome: string | null;
  nome_fantasia: string | null;
  email_mascarado: string | null;
  telefone_mascarado: string | null;
  dre_id: string | null;
  dres: { codigo: string; nome: string } | null;
  veiculos: VeiculoDoCredenciado[] | null;
  origem_dado: OrigemDoDado | null;
  sincronizado_em: string | null;
  em_contingencia: boolean | null;
  created_at: string;
  updated_at: string;
}

/** O detalhe: a linha mais o que a aba Dados Gerais mostra por inteiro. */
export interface DetalheDaApi extends LinhaDaApi {
  email: string | null;
  telefone: string | null;
  telefone2: string | null;
  termo_permissionario: string | null;
  endereco: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  cep: string | null;
  observacoes: string | null;
  conta_banco: string | null;
  conta_agencia: string | null;
  conta_numero: string | null;
  conta_operacao: string | null;
  regime_tributario: RegimeTributario | null;
}

type PaginaDaApi = Pagina<LinhaDaApi>;

interface ExportacaoDaApi {
  itens: LinhaDaApi[];
  total: number;
  truncado: boolean;
}

/**
 * A linha da API no tipo da tela. O que a lista nao traz fica nulo — inclusive os
 * marcos do ciclo, que nao sao colunas de `credenciados` (ver `contrato.ts`).
 */
export function daLinha(linha: LinhaDaApi): Credenciado {
  return {
    id: linha.id,
    slug: linha.slug,
    tipo: linha.tipo,
    status: linha.status,
    cpf_cnpj: linha.cpf_cnpj_mascarado ?? '',
    nome: linha.nome ?? '',
    nome_fantasia: linha.nome_fantasia,
    email: linha.email_mascarado,
    telefone: linha.telefone_mascarado,
    telefone2: null,
    termo_permissionario: null,
    endereco: null,
    numero: null,
    complemento: null,
    bairro: null,
    cidade: null,
    uf: null,
    cep: null,
    observacoes: null,
    conta_banco: null,
    conta_agencia: null,
    conta_numero: null,
    conta_operacao: null,
    regime_tributario: null,
    inscricao_enviada_em: null,
    pre_credenciado_em: null,
    credenciado_em: null,
    suspenso_em: null,
    motivo_suspensao: null,
    dre_id: linha.dre_id,
    dres: linha.dres,
    veiculos: Array.isArray(linha.veiculos) ? linha.veiculos : [],
    origem_dado: linha.origem_dado,
    sincronizado_em: linha.sincronizado_em,
    em_contingencia: linha.em_contingencia,
    created_at: linha.created_at,
    updated_at: linha.updated_at,
  };
}

/** O detalhe da API no tipo da tela: contatos inteiros, documento ainda mascarado. */
export function doDetalhe(detalhe: DetalheDaApi): Credenciado {
  return {
    ...daLinha(detalhe),
    email: detalhe.email,
    telefone: detalhe.telefone,
    telefone2: detalhe.telefone2,
    termo_permissionario: detalhe.termo_permissionario,
    endereco: detalhe.endereco,
    numero: detalhe.numero,
    complemento: detalhe.complemento,
    bairro: detalhe.bairro,
    cidade: detalhe.cidade,
    uf: detalhe.uf,
    cep: detalhe.cep,
    observacoes: detalhe.observacoes,
    conta_banco: detalhe.conta_banco,
    conta_agencia: detalhe.conta_agencia,
    conta_numero: detalhe.conta_numero,
    conta_operacao: detalhe.conta_operacao,
    regime_tributario: detalhe.regime_tributario,
  };
}

/**
 * O filtro como a API recebe. DRE vai repetida (`dreId=a&dreId=b`); filtro vazio nao
 * vai. Sem pagina, para a exportacao, que e o recorte inteiro.
 */
export function parametrosDoFiltro(
  filtro: Omit<FiltroDeCredenciados, 'pagina' | 'tamanho'> & {
    pagina?: number;
    tamanho?: number;
  },
): string {
  const p = new URLSearchParams();
  if (filtro.pagina !== undefined) p.set('pagina', String(filtro.pagina));
  if (filtro.tamanho !== undefined) p.set('tamanho', String(filtro.tamanho));
  const busca = filtro.busca?.trim();
  if (busca) p.set('busca', busca);
  if (filtro.situacao) p.set('situacao', filtro.situacao);
  if (filtro.tipo) p.set('tipo', filtro.tipo);
  for (const dre of filtro.dreIds ?? []) p.append('dreId', dre);
  return p.toString();
}

/**
 * A pagina de credenciados.
 *
 * `placeholderData` mantem a pagina anterior visivel enquanto a proxima chega: sem
 * ele, cada troca de pagina apaga a tabela, monta o esqueleto de novo e a tela salta
 * de altura. O estado de carregando continua existindo — ele e o da PRIMEIRA busca,
 * que e quando de fato nao ha o que mostrar.
 *
 * Tambem alimenta `comum/CredenciadoCombobox.tsx`, dos formularios: a assinatura nao
 * muda.
 */
export function useCredenciados(
  filtro: FiltroDeCredenciados,
): UseQueryResult<Pagina<Credenciado>, ErroApi> {
  return useQuery<Pagina<Credenciado>, ErroApi>({
    queryKey: CHAVES_DE_CREDENCIADOS.lista(filtro),
    queryFn: async ({ signal }) => {
      const pagina = await chamarApi<PaginaDaApi>(
        `${CAMINHO_DA_API}?${parametrosDoFiltro(filtro)}`,
        { sinal: signal },
      );
      return {
        ...pagina,
        itens: Array.isArray(pagina?.itens) ? pagina.itens.map(daLinha) : [],
      };
    },
    placeholderData: (anterior) => anterior,
    retry: naoInsistirEmRecusa,
  });
}

/**
 * Os tres numeros do topo da tela.
 *
 * Consulta separada da lista, de proposito: os indicadores falam do credenciamento
 * INTEIRO (no recorte de quem olha), e a lista fala da pagina filtrada. Derivar um do
 * outro faria "Ativos" cair para 3 assim que alguem digitasse um nome na busca — e o
 * cartao passaria a responder uma pergunta que ninguem fez.
 *
 * Cache curto porque o numero muda com a operacao do dia, e nao a cada navegacao.
 */
export function useIndicadoresDeCredenciados(): UseQueryResult<IndicadoresDeCredenciados, ErroApi> {
  return useQuery<IndicadoresDeCredenciados, ErroApi>({
    queryKey: CHAVES_DE_CREDENCIADOS.indicadores,
    queryFn: ({ signal }) =>
      chamarApi<IndicadoresDeCredenciados>(`${CAMINHO_DA_API}/indicadores`, { sinal: signal }),
    staleTime: 60_000,
    retry: naoInsistirEmRecusa,
  });
}

/**
 * O credenciado por slug, para a tela de detalhe (`CredenciadoDetail.tsx`) e a de
 * edicao (`CredenciadoEditarPage.tsx`), pelo `slug` do endereco.
 *
 * `null` (a API respondeu 404) e diferente de erro de rede: a tela de detalhe usa a
 * distincao para mostrar "Credenciado nao encontrado" em vez do estado de erro
 * generico. Nulo e nao `undefined`: o React Query trata `undefined` como falha. Fora do recorte da DRE tambem e 404 — quem nao enxerga a linha nao fica
 * sabendo que ela existe.
 */
export function useCredenciado(
  slug: string | undefined,
): UseQueryResult<Credenciado | null, ErroApi> {
  return useQuery<Credenciado | null, ErroApi>({
    queryKey: CHAVES_DE_CREDENCIADOS.detalhe(slug),
    enabled: !!slug,
    queryFn: async ({ signal }) => {
      try {
        const detalhe = await chamarApi<DetalheDaApi>(
          `${CAMINHO_DA_API}/${encodeURIComponent(slug!)}`,
          { sinal: signal },
        );
        return doDetalhe(detalhe);
      } catch (erro) {
        if (erro instanceof ErroApi && erro.situacao === 404) {
          return null;
        }
        throw erro;
      }
    },
    retry: naoInsistirEmRecusa,
  });
}

/** Representante legal, como a aba Representantes mostra. CPF sempre mascarado. */
export interface RepresentanteDoCredenciado {
  id: string;
  nome: string | null;
  cpf_mascarado: string | null;
  cargo: string | null;
  data_inicio: string | null;
  data_fim: string | null;
  ativo: boolean;
}

/**
 * Os representantes legais do credenciado (`public.representantes`). Nenhuma jornada
 * escreve essa tabela hoje: lista vazia e o estado comum, e a aba diz isso.
 */
export function useRepresentantesDoCredenciado(
  credenciadoId: string | undefined,
): UseQueryResult<RepresentanteDoCredenciado[], ErroApi> {
  return useQuery<RepresentanteDoCredenciado[], ErroApi>({
    queryKey: CHAVES_DE_CREDENCIADOS.representantes(credenciadoId),
    enabled: !!credenciadoId,
    queryFn: async ({ signal }) => {
      const lista = await chamarApi<RepresentanteDoCredenciado[]>(
        `${CAMINHO_DA_API}/${encodeURIComponent(credenciadoId!)}/representantes`,
        { sinal: signal },
      );
      return Array.isArray(lista) ? lista : [];
    },
    retry: naoInsistirEmRecusa,
  });
}

/**
 * Teto da exportacao.
 *
 * Exportar nao e paginar: quem baixa quer a planilha inteira do recorte que esta vendo.
 * Mas "inteira" sem teto e um pedido que pode voltar com trinta mil linhas e derrubar a
 * aba do navegador montando o CSV. Mil cobre o cadastro real com folga, e o teto e da
 * API (`credenciados_operacao_exportar`), que diz quando cortou.
 */
export const TETO_DA_EXPORTACAO = 1000;

export interface ResultadoDaExportacao {
  /** Linhas no arquivo. */
  quantidade: number;
  /** Linhas no recorte; maior que `quantidade` quando o teto cortou. */
  total: number;
}

/**
 * Baixa a listagem filtrada como CSV.
 *
 * BUSCA DE NOVO, COM O MESMO FILTRO E SEM PAGINA — nao exporta a pagina na tela. Uma
 * planilha com as dez linhas que por acaso estavam visiveis e a forma mais silenciosa de
 * entregar relatorio incompleto: quem recebe nao tem como saber que faltou o resto.
 *
 * O arquivo leva o que a lista mostra: documento, e-mail e telefone mascarados. A API
 * registra a exportacao em `acessos_dado_pessoal`.
 *
 * `fetchQuery` e nao `useQuery` porque isto e acao de botao, nao estado de tela. Sem
 * cache: cada clique e uma exportacao, e cada exportacao fica registrada.
 */
export function useBaixarCredenciadosCsv(): (
  filtro: FiltroDeCredenciados,
) => Promise<ResultadoDaExportacao> {
  const consultas = useQueryClient();

  return async (filtro: FiltroDeCredenciados) => {
    const { pagina: _pagina, tamanho: _tamanho, ...recorte } = filtro;
    const resposta = await consultas.fetchQuery<ExportacaoDaApi>({
      queryKey: ['credenciados', 'exportacao', recorte, Date.now()],
      queryFn: () =>
        chamarApi<ExportacaoDaApi>(`${CAMINHO_DA_API}/exportar?${parametrosDoFiltro(recorte)}`),
      staleTime: 0,
      gcTime: 0,
    });
    const linhas = Array.isArray(resposta?.itens) ? resposta.itens.map(daLinha) : [];

    exportarCsv(
      'credenciados.csv',
      ['Nome', 'CPF/CNPJ', 'DRE', 'Tipo', 'Situação', 'E-mail', 'Telefone'],
      linhas.map((credenciado) => [
        credenciado.nome,
        credenciado.cpf_cnpj,
        credenciado.dres?.codigo ?? '',
        credenciado.tipo,
        credenciado.status,
        credenciado.email ?? '',
        credenciado.telefone ?? '',
      ]),
    );

    return { quantidade: linhas.length, total: resposta?.total ?? linhas.length };
  };
}
